import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import QrScanner from 'qr-scanner';
import { Camera, X, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Minimal reusable camera QR/barcode scanner. Unlike PackageScanner, this
 * component has no opinion on what the scanned text means or where it goes —
 * it just decodes and hands the raw string to onDecode. Callers own the
 * lookup/manual-entry logic (see ReturningClient.js).
 *
 * Props:
 *   onDecode(text)   — called once per successful scan; scanner auto-stops
 *   buttonLabel      — trigger button text
 *   helpText         — short instruction shown above the trigger
 */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Help = styled.div`
  font-size: 0.8rem;
  color: #64748b;
`;

const ScanButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  box-shadow: 0 3px 8px rgba(102, 126, 234, 0.25);
  transition: all 0.15s ease;

  &:hover { transform: translateY(-1px); box-shadow: 0 5px 12px rgba(102, 126, 234, 0.35); }
  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; box-shadow: none; }
`;

const ScannerSurface = styled.div`
  position: relative;
  width: 100%;
  max-width: 280px;
  aspect-ratio: 1 / 1;
  background: #0f172a;
  border-radius: 14px;
  overflow: hidden;
  animation: ${fadeIn} 0.2s ease;

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ScannerOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  padding: 0.65rem;
  pointer-events: none;
`;

const CloseBtn = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  background: rgba(15, 23, 42, 0.6);
  color: white;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 8px;
  padding: 0.3rem 0.55rem;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
`;

const Notice = styled.div`
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 0.55rem 0.75rem;
  font-size: 0.82rem;
  display: flex;
  gap: 0.4rem;
  align-items: flex-start;
`;

const CameraQrScanner = ({ onDecode, buttonLabel = 'Scan with camera', helpText }) => {
  const { t } = useTranslation();
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState('');
  const videoRef = useRef(null);
  const qrScannerRef = useRef(null);
  const streamRef = useRef(null);

  const stopScanner = () => {
    if (qrScannerRef.current) {
      try { qrScannerRef.current.stop(); } catch (_) { /* ignore */ }
      try { qrScannerRef.current.destroy(); } catch (_) { /* ignore */ }
      qrScannerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setIsScanning(false);
  };

  useEffect(() => () => stopScanner(), []);

  const startScanner = async () => {
    setError('');
    setIsScanning(true);
    let retries = 0;
    while (!videoRef.current && retries < 10) {
      await new Promise((r) => setTimeout(r, 100));
      retries += 1;
    }
    if (!videoRef.current) {
      setIsScanning(false);
      setError('Camera view not ready. Try again.');
      return;
    }
    try {
      qrScannerRef.current = new QrScanner(
        videoRef.current,
        (result) => {
          const text = typeof result === 'string' ? result : result?.data;
          if (text) {
            stopScanner();
            onDecode(text.trim());
          }
        },
        {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
          maxScansPerSecond: 12,
          preferredCamera: 'environment',
        }
      );
      await qrScannerRef.current.start();
      if (videoRef.current?.srcObject) streamRef.current = videoRef.current.srcObject;
    } catch (err) {
      setIsScanning(false);
      setError(err?.name === 'NotAllowedError' ? 'Camera permission denied.' : 'Could not start camera.');
    }
  };

  return (
    <Wrap>
      {helpText && !isScanning && <Help>{helpText}</Help>}
      {isScanning ? (
        <ScannerSurface>
          <video ref={videoRef} playsInline muted autoPlay />
          <ScannerOverlay>
            <CloseBtn type="button" onClick={stopScanner}><X size={14} /> {t('qrScanner.cancel')}</CloseBtn>
          </ScannerOverlay>
        </ScannerSurface>
      ) : (
        <ScanButton type="button" onClick={startScanner}>
          <Camera size={16} /> {buttonLabel}
        </ScanButton>
      )}
      {error && <Notice><AlertCircle size={15} /> {error}</Notice>}
    </Wrap>
  );
};

export default CameraQrScanner;
